// Firebase Web SDK (modular). Development and tests talk to the Emulator
// Suite with the demo project id, which the SDK treats as offline-only (no
// real credentials involved). A production build gets the real project from
// VITE_FIREBASE_CONFIG (the JSON of `firebase apps:sdkconfig`, in
// .env.production.local or the FIREBASE_WEB_CONFIG variable of the deploy)
// and App Check with reCAPTCHA Enterprise. See docs/setup-firebase.md §6.
import { initializeApp, type FirebaseOptions } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

import { resolverConfig } from './configFirebase';

const env = import.meta.env;

const config = resolverConfig(env);

export const app = initializeApp(config.opciones satisfies FirebaseOptions);

/** Emulators are on by default outside production builds. */
export const usaEmuladores = config.usaEmuladores;

// App Check must be initialized before any other service is used. The
// emulators do not verify tokens, so it is production only.
if (config.recaptchaSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(config.recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
// Auth's own messages in Spanish. The password reset e-mail is ours
// (solicitarRestablecerClave + /accion), not Firebase's template.
auth.languageCode = 'es';
export const db = initializeFirestore(app, { ignoreUndefinedProperties: true });
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

if (usaEmuladores) {
  const host = env.VITE_EMULADOR_HOST ?? '127.0.0.1';
  const puerto = (v: string | undefined, porDefecto: number) => (v ? Number(v) : porDefecto);
  // Standard ports of firebase/firebase.json: one emulator suite for everyone.
  connectAuthEmulator(auth, `http://${host}:${puerto(env.VITE_EMU_AUTH, 9099)}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, host, puerto(env.VITE_EMU_FIRESTORE, 8080));
  connectStorageEmulator(storage, host, puerto(env.VITE_EMU_STORAGE, 9199));
  connectFunctionsEmulator(functions, host, puerto(env.VITE_EMU_FUNCTIONS, 5001));
}
